
import { useState, useEffect } from 'react';

interface HeaderProps {
  onContactClick: () => void;
}

export default function Header({ onContactClick }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
    setIsAboutDropdownOpen(false);
  };

  const handleAboutClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsAboutDropdownOpen(!isAboutDropdownOpen);
  };

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const bookingUrl = 'https://www.housecallpro.com/book/Blue-Team-Carpet--Dryer-Vent-Cleaning?company=80c78a4651b14af9b1112d122f1ff5d3';

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <img 
              src="https://static.readdy.ai/image/6473f32a47a131b496242da22ce546c9/6b23656feb7c8e7c7f58fff4bf1c9ee9.jpeg" 
              alt="Blue Team Home Services Logo" 
              className="h-10 sm:h-12 w-auto mr-2 sm:mr-3"
            />
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-900" style={{ fontFamily: '"Pacifico", serif' }}>
                Blue Team
              </h1>
              <p className="text-xs text-gray-600 hidden sm:block">Home Services</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <a href="#" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              Home
            </a>
            <div className="relative">
              <button
                onClick={handleAboutClick}
                className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer flex items-center"
              >
                About
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className={`ri-arrow-down-s-line transition-transform ${isAboutDropdownOpen ? 'rotate-180' : ''}`}></i>
                </div>
              </button>
              {isAboutDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border py-2 z-50">
                  <button
                    onClick={() => scrollToSection('faq')}
                    className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 cursor-pointer"
                  >
                    FAQ
                  </button>
                  <button
                    onClick={() => scrollToSection('blog')}
                    className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 cursor-pointer"
                  >
                    Blog
                  </button>
                  <a
                    href="#products"
                    className="block px-4 py-2 text-gray-700 hover:bg-gray-50 cursor-pointer"
                  >
                    Products
                  </a>
                </div>
              )}
            </div>
            <a href="#services" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              Services
            </a>
            <button
              onClick={() => scrollToSection('testimonials')}
              className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            >
              Testimonials
            </button>
            <a href="#contact" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              Contact
            </a>
          </nav>

          {/* Right side buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Phone number */}
            <a 
              href="tel:+16197482446" 
              className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer flex items-center"
            >
              <i className="fa fa-phone mr-2"></i>
              (619) 748-2446
            </a>
            {/* Book Now Button */}
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-teal-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer inline-block"
            >
              Book Now
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-gray-700 cursor-pointer p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Toggle menu"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
            </div>
          </button>
        </div>

        {/* Mobile Navigation - Full Screen Overlay */}
        {isMenuOpen && (
          <div className="md:hidden fixed inset-0 top-16 bg-white z-40 overflow-y-auto">
            <div className="container mx-auto px-4 py-6">
              <div className="flex flex-col space-y-4">
                <a 
                  href="#" 
                  onClick={() => setIsMenuOpen(false)}
                  className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer py-3 text-lg border-b border-gray-100"
                >
                  Home
                </a>
                <div className="border-b border-gray-100">
                  <button
                    onClick={handleAboutClick}
                    className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer flex items-center justify-between w-full py-3 text-lg"
                  >
                    About
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`ri-arrow-down-s-line text-xl transition-transform ${isAboutDropdownOpen ? 'rotate-180' : ''}`}></i>
                    </div>
                  </button>
                  {isAboutDropdownOpen && (
                    <div className="pb-3 space-y-2">
                      <button
                        onClick={() => scrollToSection('faq')}
                        className="block text-gray-600 hover:text-teal-600 cursor-pointer py-2 pl-4 text-base w-full text-left"
                      >
                        FAQ
                      </button>
                      <button
                        onClick={() => scrollToSection('blog')}
                        className="block text-gray-600 hover:text-teal-600 cursor-pointer py-2 pl-4 text-base w-full text-left"
                      >
                        Blog
                      </button>
                      <a
                        href="#products"
                        onClick={() => setIsMenuOpen(false)}
                        className="block text-gray-600 hover:text-teal-600 cursor-pointer py-2 pl-4 text-base"
                      >
                        Products
                      </a>
                    </div>
                  )}
                </div>
                <a 
                  href="#services" 
                  onClick={() => setIsMenuOpen(false)}
                  className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer py-3 text-lg border-b border-gray-100"
                >
                  Services
                </a>
                <button
                  onClick={() => scrollToSection('testimonials')}
                  className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer text-left py-3 text-lg border-b border-gray-100"
                >
                  Testimonials
                </button>
                <a 
                  href="#contact" 
                  onClick={() => setIsMenuOpen(false)}
                  className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer py-3 text-lg border-b border-gray-100"
                >
                  Contact
                </a>
                
                {/* Mobile contact info */}
                <div className="pt-6 space-y-4">
                  <a 
                    href="tel:+16197482446" 
                    className="flex items-center justify-center text-teal-600 hover:text-teal-700 font-semibold cursor-pointer py-3 bg-teal-50 rounded-lg text-lg"
                  >
                    <i className="fa fa-phone mr-3"></i>
                    (619) 748-2446
                  </a>
                  {/* Mobile Book Now Button */}
                  <a
                    href={bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-teal-600 text-white px-6 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer w-full text-center shadow-lg block"
                  >
                    Book Appointment
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
